import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Bug, ShieldAlert, ArrowRight, Camera, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fetchPests } from "@/lib/api";
import DiagnoseDialog from "@/components/DiagnoseDialog";
import { toast } from "sonner";

const SEVERITY_COLOR = {
    serious:    { bg: "bg-[var(--vm-terracotta)]/15", text: "text-[var(--vm-terracotta)]", border: "border-[var(--vm-terracotta)]/40" },
    common:     { bg: "bg-[var(--vm-secondary)]/20", text: "text-[var(--vm-secondary)]",  border: "border-[var(--vm-secondary)]/40" },
    occasional: { bg: "bg-[var(--vm-sage)]/15",       text: "text-[var(--vm-sage)]",       border: "border-[var(--vm-sage)]/40" },
};

export default function Pests() {
    const [pests, setPests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [severity, setSeverity] = useState("all");
    const [identifyTarget, setIdentifyTarget] = useState(null);
    const identifyInputRef = useRef(null);

    const stagePhoto = (file) => {
        if (!file) return;
        const ok = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
        if (!ok.includes((file.type || "").toLowerCase())) {
            toast.error("Please use a JPG, PNG, WEBP or HEIC photo.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Photo too large (max 10 MB).");
            return;
        }
        const dataUri = URL.createObjectURL(file);
        setIdentifyTarget({ file, dataUri, filename: file.name });
    };

    useEffect(() => {
        fetchPests()
            .then((d) => setPests(d.pests || []))
            .catch(() => setPests([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return pests.filter((p) => {
            if (severity !== "all" && p.severity !== severity) return false;
            if (!needle) return true;
            const hay = `${p.name} ${p.kind} ${p.damage} ${(p.crops_affected || []).join(" ")}`.toLowerCase();
            return hay.includes(needle);
        });
    }, [pests, q, severity]);

    return (
        <div className="space-y-10" data-testid="pests-page">
            <PageHeader
                overline="Reference library"
                title="Pest guide"
                description="Identify what's munching your plants. 30 common UK & European garden pests with organic controls and life-cycle notes."
            />

            {/* Identify-by-photo card */}
            <section className="journal-card p-6" data-testid="pest-identify-card">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--vm-accent)]/10 flex items-center justify-center shrink-0">
                        <Camera className="w-6 h-6 text-[var(--vm-accent)]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">
                            Not sure what bug that is?
                        </h3>
                        <p className="text-sm text-[var(--vm-text-muted)] mt-1 mb-3">
                            Snap a photo and rooteed will identify it, tell you if it's a friend or a pest, and link to the catalog entry.
                        </p>
                        <input
                            ref={identifyInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                            className="hidden"
                            data-testid="pest-photo-input"
                            onChange={(e) => { stagePhoto(e.target.files?.[0]); e.target.value = ""; }}
                        />
                        <Button
                            onClick={() => identifyInputRef.current?.click()}
                            data-testid="pest-identify-btn"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                        >
                            <Sparkles className="w-4 h-4 mr-2" /> Identify by photo
                        </Button>
                    </div>
                </div>
            </section>
            {/* Search + severity filter */}
            <section className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[16rem]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vm-text-muted)]" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, kind or crop…"
                        data-testid="pest-search-input"
                        className="pl-9 bg-[var(--vm-card)] border-[var(--vm-border)]"
                    />
                </div>
                <div className="flex gap-1" data-testid="pest-severity-filter">
                    {["all", "serious", "common", "occasional"].map((s) => (
                        <Button
                            key={s}
                            type="button"
                            size="sm"
                            variant={severity === s ? "default" : "outline"}
                            onClick={() => setSeverity(s)}
                            data-testid={`pest-severity-${s}`}
                            className={`rounded-full capitalize ${severity === s ? "bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]" : "border-[var(--vm-border)]"}`}
                        >
                            {s}
                        </Button>
                    ))}
                </div>
            </section>

            {loading ? (
                <p className="text-[var(--vm-text-muted)]">Loading pests…</p>
            ) : filtered.length === 0 ? (
                <div className="journal-card p-8 text-center" data-testid="pests-empty">
                    <Bug className="w-8 h-8 text-[var(--vm-text-muted)] mx-auto mb-2" />
                    <p className="text-sm text-[var(--vm-text-muted)]">No pests match that search.</p>
                </div>
            ) : (
                <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pests-grid">
                    {filtered.map((p) => {
                        const sev = SEVERITY_COLOR[p.severity] || SEVERITY_COLOR.common;
                        return (
                            <Link
                                key={p.slug}
                                to={`/pests/${p.slug}`}
                                data-testid={`pest-card-${p.slug}`}
                                className="journal-card overflow-hidden group hover:border-[var(--vm-accent)] transition-colors"
                            >
                                <div
                                    className="relative h-40 w-full overflow-hidden"
                                    style={{ background: p.photo?.avg_color || "var(--vm-input)" }}
                                    data-testid={`pest-photo-${p.slug}`}
                                >
                                    {p.photo?.thumb_url ? (
                                        <img
                                            src={p.photo.thumb_url}
                                            alt={p.photo.alt || p.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center text-5xl opacity-60">{p.emoji}</div>
                                    )}
                                    <span className={`absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${sev.bg} ${sev.text} ${sev.border} backdrop-blur-sm bg-opacity-80`}>
                                        {p.severity}
                                    </span>
                                </div>
                                <div className="p-5">
                                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)] mb-1 group-hover:text-[var(--vm-accent)]">{p.name}</h3>
                                    <p className="text-xs uppercase tracking-wider text-[var(--vm-sage)] mb-2">{p.kind}</p>
                                    <p className="text-sm text-[var(--vm-text-secondary)] line-clamp-3">{p.damage}</p>
                                    <div className="flex flex-wrap gap-1 mt-3">
                                        {(p.crops_affected || []).slice(0, 3).map((c) => (
                                            <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--vm-input)] text-[var(--vm-text-muted)]">{c}</span>
                                        ))}
                                    </div>
                                    {p.photo?.photographer && (
                                        <div className="mt-3 text-[10px] text-[var(--vm-text-muted)]">
                                            Photo by {p.photo.photographer} on Pexels
                                        </div>
                                    )}
                                    <div className="mt-2 flex items-center gap-1 text-xs text-[var(--vm-accent)] opacity-0 group-hover:opacity-100 transition-opacity">
                                        Read more <ArrowRight className="w-3 h-3" />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </section>
            )}

            <div className="journal-card p-5 flex items-start gap-4" data-testid="beneficials-cta">
                <ShieldAlert className="w-6 h-6 text-[var(--vm-sage)] shrink-0 mt-1" />
                <div className="flex-1">
                    <h3 className="font-serif-display text-xl text-[var(--vm-text)]">The natural counter-attack</h3>
                    <p className="text-sm text-[var(--vm-text-muted)]">
                        Encourage beneficial insects before you reach for any spray — ladybirds,
                        hoverflies, lacewings and parasitic wasps can control most pests for free.
                    </p>
                </div>
                <Button asChild className="rounded-full bg-[var(--vm-sage)] hover:opacity-90 text-[var(--vm-bg)] shrink-0">
                    <Link to="/beneficials" data-testid="go-to-beneficials-btn">Browse beneficials</Link>
                </Button>
            </div>

            <DiagnoseDialog
                open={!!identifyTarget}
                onClose={() => {
                    if (identifyTarget?.dataUri) URL.revokeObjectURL(identifyTarget.dataUri);
                    setIdentifyTarget(null);
                }}
                source={identifyTarget || {}}
                mode="pest_match"
            />
        </div>
    );
}
